import { useRef, useState, useEffect } from "react";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import { KineticLine } from "./KineticLine";
import { scrollToSection } from "@/lib/lenis";
import { CONTACT } from "@/lib/contact";

const HERO_IMAGES = [
  "https://images.pexels.com/photos/16323438/pexels-photo-16323438.jpeg?auto=compress&cs=tinysrgb&w=1600",
  "https://images.pexels.com/photos/35741386/pexels-photo-35741386.jpeg?auto=compress&cs=tinysrgb&w=1600",
  "https://images.unsplash.com/photo-1573998648748-fa6ef521cdf9?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  "https://images.unsplash.com/photo-1553413077-190dd305871c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  "https://images.unsplash.com/photo-1555396273-367ea4eb4db5?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
];

export const Hero = () => {
  const ref = useRef(null);
  const [index, setIndex] = useState(0);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const opacity = useTransform(scrollYProgress, [0, 1], [1, 0.4]);

  useEffect(() => {
    const timer = setInterval(() => setIndex((i) => (i + 1) % HERO_IMAGES.length), 4000);
    return () => clearInterval(timer);
  }, []);

  return (
    <section
      ref={ref}
      id="home"
      className="relative h-screen min-h-[720px] overflow-hidden bg-[#0A1128] flex items-center"
    >
      <motion.div style={{ y, opacity }} className="absolute inset-0">
        <AnimatePresence>
          <motion.img
            key={index}
            src={HERO_IMAGES[index]}
            alt="A business Grow High Accounting works with"
            initial={{ opacity: 0, scale: 1.06 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
            className="absolute inset-0 w-full h-full object-cover"
            data-testid={`hero-slide-${index}`}
          />
        </AnimatePresence>
        <div className="absolute inset-0 bg-[#0A1128]/75" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A1128] via-[#0A1128]/60 to-transparent" />
      </motion.div>
      <div className="grain-overlay" />

      <div className="absolute bottom-24 left-6 md:left-12 z-10 flex gap-2" data-testid="hero-slide-indicators">
        {HERO_IMAGES.map((_, i) => (
          <span
            key={i}
            data-testid={`hero-dot-${i}`}
            className={`h-1.5 rounded-full transition-all duration-500 ${
              i === index ? "w-8 bg-[#C5A059]" : "w-1.5 bg-white/30"
            }`}
          />
        ))}
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 w-full pt-24">
        <p className="overline text-[#C5A059] mb-6" data-testid="hero-overline">
          Accounting · Audit · Compliance · Chennai
        </p>
        <h1 className="font-serif font-medium text-5xl md:text-6xl lg:text-[5rem] tracking-tighter leading-[1.05] text-white max-w-4xl">
          <KineticLine text="Reliable Accounting," delay={0.1} />
          <KineticLine text="Auditing & Compliance." delay={0.25} />
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.6 }}
          data-testid="hero-subheadline"
          className="mt-8 text-slate-300 text-base md:text-lg max-w-xl leading-relaxed"
        >
          You focus on running your business. Our team will make sure your books are accurate, your
          taxes are filed, and your company meets all legal requirements — on time.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.6 }}
          className="mt-10 flex flex-wrap gap-4"
        >
          <a
            href={CONTACT.whatsappHref}
            target="_blank"
            rel="noreferrer"
            data-testid="hero-whatsapp-btn"
            className="inline-flex items-center gap-2 rounded-full bg-[#25D366] text-white px-7 py-3.5 text-base font-medium hover:bg-[#20bd5a] transition-colors duration-300"
          >
            Message us on WhatsApp
          </a>
          <button
            onClick={() => scrollToSection("contact")}
            data-testid="hero-callback-btn"
            className="inline-flex items-center gap-2 rounded-full bg-white text-[#0A1128] px-7 py-3.5 text-base font-medium hover:bg-slate-200 transition-colors duration-300"
          >
            Request a Callback
          </button>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 0.6 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/50 text-xs tracking-[0.3em] uppercase"
      >
        Scroll
      </motion.div>
    </section>
  );
};
